import { bodyOverlaps, mean, spansOverlap } from '../geom'
import { isHarmful } from '../hazards/step'
import { JUMP } from '../inputBits'
import type { Aabb } from '../track/types'
import { NO_TICK } from '../world'
import type { World } from '../world'
import { edgeKey, inZone, landPlatform, ride, snapRamps, trapAt } from './rolloutBody'
import type { Sim } from './rolloutBody'
import { narrow } from './scene'
import type { Scene } from './scene'

/**
 * What the bot does on the action tick. `run` presses nothing; `tap` and `hold` press JUMP,
 * which is the double jump when the body is already in the air with an air jump left. `double`
 * holds a full jump, releases at its apex and presses again (a planned double jump).
 */
export type Action = 'run' | 'tap' | 'hold' | 'double'

/** Why a rollout went wrong: off an edge, into a wall, or into something that hits. */
export type Trouble = 'fall' | 'wall' | 'hazard' | 'trap'

export interface Plan {
  readonly action: Action
  /** Ticks of plain running before the action tick. */
  readonly delay: number
  /** JUMP stays down from the start: a full-height jump already in the air. */
  readonly keepHold: boolean
  /** Success once the body stands on support with its left edge past this x after acting. */
  readonly goalX: number
  readonly horizon: number
  /** Optional box (a crate) the rollout reports touching. */
  readonly touch: Aabb | null
  /** Also success once the body stands on support at or above this y after acting (a climb). */
  readonly climbTo?: number
  /** Landing this much below the last support counts as a fall (stay on upper routes). */
  readonly maxDropPx?: number
  /** The action jump must leave from support or coyote time (never spend the air jump on it). */
  readonly groundOnly?: boolean
}

export interface Outcome {
  /** Goal reached; for a goal of `Infinity`, the horizon ended without trouble. */
  readonly ok: boolean
  readonly trouble: Trouble | null
  /** Rollout tick (0 = this tick) the trouble happens. */
  readonly at: number
  /** Where the trouble starts and where it is cleared (left edge past it counts as cleared). */
  readonly fromX: number
  readonly clearX: number
  /** Identity of the thing causing it (stable while the bot approaches). */
  readonly key: number
  /** The action really fired (a jump needs support, coyote time or an air jump left). */
  readonly acted: boolean
  /** A bounce pad launched the body before any trouble. */
  readonly bounced: boolean
  readonly touched: boolean
}

/**
 * Predict player `i` under `plan` with the step-1…5 rules for one body (input, jump and double
 * jump, velocity, platforms carried, X then Y against solids, one-way platforms, ramps, bounce
 * pads, hazards at `tick + 1`, traps and spike balls ahead). Other players are ignored and the world
 * is not touched. It mirrors the real step closely but is only a forecast: the bot re-plans
 * every tick. A ride (running, or due from a summon in progress) is mirrored too: its speed,
 * slow-strip immunity, higher jumps, glide, bulldozing through obstacles, burrow and pop-out,
 * and its immunity (a rider forecasts no hazard, trap or spike-ball trouble).
 */
export function rollout(world: World, i: number, wide: Scene, plan: Plan): Outcome {
  const { cfg, track } = world
  const scene = narrow(world, wide, i, plan.horizon)
  const p = world.players[i]!
  const b: Sim = { x: p.x, y: p.y, w: p.w, h: p.h, vy: p.vy }
  let grounded = p.grounded
  let coyote = p.coyoteUntil
  let held = p.jumpHeldTicks
  let jumpsLeft = p.jumpsLeft
  let prevJump = (p.prevBits & JUMP) !== 0
  let acted = plan.action === 'run'
  // A jump fired in this rollout: a wall it meets may still be cleared by rising past it.
  let jumped = false
  // `double`: 0 before the jump, 1 holding it, 2 released at the apex, 3 holding the air jump.
  let stage = 0
  // Feet height when last on support, for the drop check.
  let supportY = p.grounded ? p.y : Infinity
  const climbTo = plan.climbTo ?? -Infinity
  const maxDrop = plan.maxDropPx ?? Infinity
  let bounced = false
  let touched = false
  // Walked off an edge (not jumped or bounced) and not landed since; the edge is where the body
  // first had no support. Mid-coyote at the start, it is rebuilt from the walk-off tick.
  let walkedOff = !p.grounded && p.coyoteUntil !== NO_TICK
  let edgeX = walkedOff ? p.x - p.vx * (world.tick - 1 - (p.coyoteUntil - cfg.coyoteTicks)) : p.x
  const horizon = scene.horizon
  // The ride window [rideFrom, rideTo): riding now, or starting when a summon's wind-up ends.
  const rideDef = p.rideId === null ? null : cfg.rides.byId[p.rideId]
  const ridingNow = p.ridingUntil > world.tick
  const summoning = p.summonAtTick !== NO_TICK
  const rideFrom = ridingNow ? world.tick : summoning ? p.summonAtTick : Infinity
  const rideTo = ridingNow
    ? p.ridingUntil
    : summoning
      ? p.summonAtTick + cfg.rides.rideDurationTicks
      : -Infinity
  let burrowUntil = p.burrowUntil

  for (let k = 0; k < horizon; k++) {
    const t = world.tick + k
    const riding = rideDef !== null && t >= rideFrom && t < rideTo ? rideDef : null
    if (t === rideTo) jumpsLeft = 1
    const burrowed = burrowUntil > t
    let fire = k === plan.delay
    let jumpBit =
      plan.keepHold ||
      (plan.action === 'hold' && k >= plan.delay) ||
      (plan.action === 'tap' && fire)
    if (plan.action === 'double' && k >= plan.delay) {
      if (stage === 0) stage = 1
      else if (stage === 1 && b.vy >= 0) stage = 2
      else if (stage === 2) {
        stage = 3
        fire = true
      }
      jumpBit = stage === 1 || (stage === 3 && (fire || b.vy < 0))
    }

    // Step 1: jump release, jump (first from support or coyote, else the air jump).
    if (held > 0) {
      if (b.vy >= 0) held = 0
      else if (jumpBit) held++
      else {
        b.vy = (b.vy * cfg.jumpReleaseKeepPct) / 100
        held = 0
      }
    }
    const jumpPress = jumpBit && !prevJump
    prevJump = jumpBit
    const fromGround = grounded || t <= coyote
    const burrowNow = riding?.ability === 'burrow' && burrowUntil === 0 && grounded
    const airJump = jumpsLeft > 0 && riding?.ability !== 'glide'
    if (fire && jumpPress && !burrowed && burrowNow) {
      burrowUntil = t + riding.burrowTicks
      acted = true
    } else if (fire && jumpPress && !burrowed && (fromGround || (airJump && !plan.groundOnly))) {
      const scale = riding ? riding.jumpVelocityPct / 100 : 1
      const v = fromGround ? cfg.jumpVelocityPxPerTick : cfg.doubleJumpVelocityPxPerTick
      b.vy = -v * scale
      jumpsLeft = fromGround ? 1 : jumpsLeft - 1
      grounded = false
      coyote = NO_TICK
      held = 1
      acted = true
      jumped = true
      walkedOff = false
    }

    // Step 2: velocities.
    const boosted = p.boostUntil > t
    const boost = boosted ? p.boostPct : 0
    const pct = 100 + (riding ? Math.min(cfg.rides.maxSpeedPct, riding.speedPct + boost) : boost)
    let vx = (cfg.baseSpeedPxPerTick * pct) / 100
    if (!(boosted && p.boostIgnoresMud) && !riding?.ignoresSlow) {
      if (inZone(world, scene, b, 'mud')) vx = (vx * cfg.mudSpeedPct) / 100
      if (inZone(world, scene, b, 'slowStrip')) vx = (vx * cfg.slowStripPct) / 100
    }
    const vy0 = b.vy
    const glide = riding?.ability === 'glide' ? riding : null
    const maxFall = (cfg.maxFallSpeedPxPerTick * (glide ? glide.fallSpeedPct : 100)) / 100
    b.vy = Math.min(vy0 + cfg.gravityPxPerTick2, maxFall)
    if (glide && !grounded && jumpBit) b.vy = Math.min(b.vy, -glide.glideLiftPxPerTick)
    const dy = mean(vy0, b.vy)

    // Step 3: carried by a platform, X against solids, Y against solids, platforms, ramps.
    const prevFoot = b.y
    if (grounded) ride(world, scene, b, t)
    const wasGrounded = grounded
    const through = riding?.ability === 'bulldoze'
    b.x += vx
    for (const s of scene.solids) {
      const a = track.solids[s]!
      if ((through && a.obstacle) || !bodyOverlaps(b, a)) continue
      if (grounded && b.y - a.y <= cfg.rampSnapPx) {
        b.y = a.y
        continue
      }
      // Off an edge and into the far side's wall: the edge is the obstacle.
      if (walkedOff)
        return outcome(false, 'fall', k, edgeX, edgeX, edgeKey(edgeX), acted, bounced, touched)
      if (!jumped) return outcome(false, 'wall', k, a.x, a.x, s, acted, bounced, touched)
      // Jumping: pushed out like the real X pass; the rise may still carry it over.
      b.x = vx > 0 ? a.x - b.w : a.x + a.w
    }
    b.y += dy
    let landed = false
    if (dy !== 0) {
      for (const s of scene.solids) {
        const a = track.solids[s]!
        if ((through && a.obstacle) || !bodyOverlaps(b, a)) continue
        if (dy > 0) {
          b.y = a.y
          landed = true
        } else b.y = a.y + a.h + b.h
        b.vy = 0
      }
    }
    if (landPlatform(world, scene, b, prevFoot, t)) {
      b.vy = 0
      landed = true
    }
    if (snapRamps(world, scene, b, landed)) {
      b.vy = 0
      landed = true
    }
    let launched = false
    if (landed && inZone(world, scene, b, 'bouncePad')) {
      b.vy = -cfg.bounceVelocityPxPerTick
      held = 0
      landed = false
      launched = true
      bounced = true
    }
    if (landed && b.y - supportY >= maxDrop) {
      return outcome(false, 'fall', k, edgeX, edgeX, edgeKey(edgeX), acted, bounced, touched)
    }
    grounded = landed
    if (landed) {
      jumpsLeft = 1
      supportY = b.y
    }
    if (landed || launched) walkedOff = false
    if (launched) coyote = NO_TICK
    else if (wasGrounded && !landed && burrowUntil > t) {
      // A burrowed rider pops out of a pit edge with a first jump.
      burrowUntil = t
      b.vy = -cfg.jumpVelocityPxPerTick
      jumpsLeft = 1
      coyote = NO_TICK
      jumped = true
    } else if (wasGrounded && !landed) {
      coyote = t + cfg.coyoteTicks
      edgeX = b.x
      walkedOff = true
    }
    if (!grounded && (b.y > scene.lowestTop || b.y > track.killY)) {
      return outcome(false, 'fall', k, edgeX, edgeX, edgeKey(edgeX), acted, bounced, touched)
    }

    // Steps 4–5: hazards at `t + 1` (the `hazardAt` lookup, inlined), traps, spike balls ahead.
    if (p.invulnUntil <= t && !riding) {
      for (const hk of scene.hazards) {
        const h = track.hazards[hk]!
        if (!spansOverlap(b.x, b.w, h.x, h.w)) continue
        const c = (t + 1 + h.phaseTicks) % h.periodTicks
        const box = h.boxes[c]!
        if (isHarmful(h.phases[c]!) && bodyOverlaps(b, box)) {
          return outcome(false, 'hazard', k, box.x, box.x + box.w, hk, acted, bounced, touched)
        }
      }
      const hitBy = trapAt(world, i, b, t, k)
      if (hitBy)
        return outcome(
          false,
          'trap',
          k,
          hitBy.x,
          hitBy.x + hitBy.w,
          hitBy.id,
          acted,
          bounced,
          touched,
        )
    }
    if (plan.touch && bodyOverlaps(b, plan.touch)) touched = true
    if (acted && grounded && (b.x > plan.goalX || b.y <= climbTo)) {
      return outcome(true, null, k, b.x, b.x, -1, acted, bounced, touched)
    }
  }
  // A climb must end up high; plain running only needs to stay out of trouble.
  const ok = (plan.goalX === Infinity && plan.climbTo === undefined) || bounced
  return outcome(ok, null, horizon, b.x, b.x, -1, acted, bounced, touched)
}

function outcome(
  ok: boolean,
  trouble: Trouble | null,
  at: number,
  fromX: number,
  clearX: number,
  key: number,
  acted: boolean,
  bounced: boolean,
  touched: boolean,
): Outcome {
  return { ok, trouble, at, fromX, clearX, key, acted, bounced, touched }
}
